// Plain-language explanations for the FlowEngine (3Dflow) model-generation settings, shown as hover
// tooltips next to each parameter in the admin editor. Keyed by the parameter key (same key across
// categories means the same concept). Best-effort guidance for tuning — refine as the team learns
// more; a missing key simply shows no icon.
//
// Pipeline stages: BundleAdjustment = camera pose/calibration refinement · Samantha/Scamantha =
// structure-from-motion (feature matching + sparse cloud + camera alignment) · Stereo = dense point
// cloud (multi-view stereo) · Surface = meshing · Texture = texturing · Orthophoto = orthophoto/DTM ·
// Wrapper = FlowEngine's mesh morph/constraint step · CameraIntrinsics/Workspace = runtime-populated.

export const PARAM_DOCS = {
  // --- BundleAdjustment ---
  AdjustInternals: "Refine each camera's internal parameters (focal length, principal point) during bundle adjustment, not just its pose.",
  AdjustRadialDistortion: "Solve for lens radial (barrel/pincushion) distortion during bundle adjustment.",
  AdjustTangentialDistortion: "Solve for lens tangential distortion (from an off-centre/tilted lens) during bundle adjustment.",
  MaxIterations: "Maximum optimiser iterations per bundle-adjustment solve.",
  MaxOuterIterations: "Maximum outer refine-and-re-solve passes of bundle adjustment.",
  MaxReprojectionError: "Reprojection error (pixels) above which a point/observation is treated as an outlier and dropped.",
  MaximumTracksPerCamera: "Cap on feature tracks kept per camera in the solve; limits cost in feature-dense scenes.",
  MinIterations: "Minimum optimiser iterations before a solve is allowed to stop.",
  RemoveInvalidCameras: "Drop cameras that fail to resolve a reliable pose instead of keeping them.",
  TwoStepsFocalAdjustment: "Refine focal length in two stages (coarse then fine) for more stable convergence.",

  // --- Cuda ---
  DeviceList: "Which GPU device index(es) to use, comma-separated (0 = first GPU).",

  // --- Orthophoto ---
  AggregationFactor: "Downsampling applied when building the orthophoto; higher = coarser and faster.",
  BorderCompletationWindowSize: "Window size used to fill/complete orthophoto borders (0 = off).",
  ColorBalanceMaxStrenght: "Maximum strength of colour balancing across images in the orthophoto.",
  CropBordersWithConvexHull: "Crop the orthophoto to the convex hull of the data instead of a rectangle.",
  DTMClassThreshold: "Ground vs non-ground classification threshold for the digital terrain model.",
  DTMClothResolution: "Cloth-simulation resolution (m) for DTM ground extraction; smaller = finer terrain.",
  DTMIterations: "Iterations of the cloth simulation used to extract the DTM ground surface.",
  DTMRigidness: "Rigidness of the DTM cloth simulation; higher resists draping into fine detail.",
  DTMSmoothing: "Smoothing applied to the extracted DTM surface.",
  DTMTimeStep: "Time step of the DTM cloth simulation; affects convergence and stability.",
  GenerateOrthophoto: "Whether to produce an orthophoto at all (1 = yes).",
  Height: "Output orthophoto height in pixels.",
  Width: "Output orthophoto width in pixels.",
  MaxCamerasPerPixel: "How many source cameras may contribute to each orthophoto pixel.",
  PointRadius: "Splat radius (pixels) each point covers when rasterising the orthophoto.",
  UseColorBalance: "Enable colour balancing across images in the orthophoto.",
  UseMultiband: "Use multiband blending for seamless orthophoto seams.",

  // --- Samantha / Scamantha (structure-from-motion) ---
  ANNNumberOfFeatures: "Number of features used by approximate-nearest-neighbour feature matching.",
  BoundingBoxType: "How the reconstruction bounding box is determined (preset mode).",
  DownscaleFactor: "Image downscale before this stage; 1 = full resolution, higher = faster with less detail.",
  FarPointsRatio: "Fraction of very distant points to keep; trims far outliers (0 = drop them).",
  FinalBa: "Run a final full bundle adjustment at the end of SfM.",
  FinalMaxReprojectionError: "Reprojection-error threshold (pixels) for the final bundle adjustment.",
  GlobalReconstruction: "Use a global (vs incremental/sequential) SfM reconstruction strategy.",
  KeypointNumberOfLevels: "Scale-space levels for keypoint detection; more = features found across more scales.",
  KeypointNumberOfLevelsPerOctave: "Keypoint scale levels per octave; finer scale sampling for detection.",
  MatchingBreakup: "Split matching into batches to bound memory and time.",
  MatchingPreemptiveNeighborFeatures: "Features used in the preemptive pass that rejects unlikely image pairs early.",
  MaxCamerasPerChunk: "Maximum cameras processed per chunk; larger uses more memory, smaller splits the work.",
  MaxKeypointsPerImage: "Cap on keypoints detected per image; higher = more matches but slower.",
  MaxOuterLoopBAIterations: "Bundle-adjustment loops interleaved with incremental reconstruction.",
  MaxResolution: "Maximum image dimension (pixels) used for this stage; larger images are downscaled to it.",
  MinimumTrackLength: "Minimum number of images a 3D point must be seen in to be kept.",
  NCamerasToFixIntrinsics: "After this many cameras, lock intrinsics to stabilise the solve.",
  NumberOfMatchViews: "Neighbouring views each image is matched against (sequential).",
  NumberOfMatchViewsN: "Additional non-sequential views considered for matching.",
  NumberOfMaxMatchViews: "Upper cap on how many views any image is matched against.",
  ReprojectionThreshold: "Reprojection-error threshold (pixels) for accepting a match/point during SfM.",
  SavePartialReconstructions: "Save intermediate reconstructions (debugging).",
  StructSequence: "Treat the input as an ordered sequence (video-style) to speed up matching.",
  TiePointsPerCameraOnBA: "Tie points per camera used during bundle adjustment.",
  TiePointsPerCameraOnFinalBA: "Tie points per camera used in the final bundle adjustment.",
  UngroupCalibrationsOnFinalBA: "Give each camera its own calibration in the final BA instead of a shared group.",
  AccuracyThreshold: "Accuracy threshold for the fast matcher; lower = stricter matches.",
  MinInliersRatio: "Minimum inlier ratio for an image pair to be accepted as a valid match.",

  // --- Stereo (dense point cloud) ---
  AdditionalPyramidLevels: "Extra image-pyramid levels for dense matching; helps large or low-texture areas.",
  DepthMapSpeedup: "Speed-vs-quality tradeoff for depth maps; higher = faster but less accurate.",
  DepthMapSpeedupOnLastLevel: "Speedup applied specifically on the finest pyramid level.",
  DepthNoiseFiltering: "Strength of noise filtering applied to the depth maps.",
  EpipolarLineStep: "Sampling step along the epipolar line when searching for dense matches.",
  EpipolarLineStepOnLastLevel: "Epipolar sampling step on the finest level.",
  HyperplaneMatching: "Use hyperplane-based matching for dense correspondence.",
  MaxResolutionPercent: "Percent of source resolution used for the dense cloud; higher = denser and slower.",
  NearSaMPoints: "Number of nearby points used in the surface-aware matching step.",
  NoiseFiltering: "Strength of outlier/noise filtering on the dense cloud.",
  NumberOfNearestCameras: "How many nearest cameras are fused per depth map.",
  RawOutput: "Output the raw dense result without post-filtering.",
  WindowSize: "Matching window size (pixels) for dense correspondence; larger = smoother, less fine detail.",

  // --- Surface (meshing) ---
  ComponentSizeTrimScale: "Remove disconnected mesh islands smaller than this scale (0 = keep all).",
  EdgeLengthTrimScale: "Trim triangles whose edges exceed this scale — removes stretched webbing across gaps.",
  PCEdgeFilterStrength: "Edge-preserving filter strength applied to the point cloud before meshing.",
  PCEnhancePostFilterStrength: "Post-filter enhancement strength on the point cloud.",
  PCMaxIterations: "Iterations of the point-cloud surface solver.",
  PCMaxResolutionPercent: "Percent of resolution used for meshing; higher = finer mesh and slower.",
  PCPostDecimationFactor: "Decimation applied after meshing (1 = none); lower triangle count at higher values.",
  PCTargetReprojectionArea: "Target per-triangle reprojection area driving mesh density.",
  SmoothingFilter: "Enable the mesh smoothing filter.",
  SmoothingIterations: "Number of mesh smoothing passes.",
  Thickness: "Assumed surface thickness used by the reconstruction (affects watertight/offset behaviour).",
  UsePhotoconsistency: "Use photo-consistency to guide the result — sharper where source images agree.",

  // --- Texture ---
  BlurrinessDetection: "Detect and avoid blurry source images when selecting texture/orthophoto pixels.",
  ColorBalance: "Colour-balance images before texturing for consistent appearance.",
  ColorBalanceHalfWindowSize: "Half-window size for texture colour balancing.",
  ColorBalanceSoftenEdgesHalfWindowSize: "Half-window for softening seams during colour balancing.",
  GenerateFloat: "Generate a floating-point (HDR) texture instead of 8-bit.",
  ImageResolutionPercent: "Fraction of source image resolution used when sampling the texture.",
  MaxCamerasPerTriangle: "How many cameras may colour a triangle (1 = single best view).",
  MaxCompatibleCamerasPerTriangle: "Maximum candidate cameras considered compatible for a triangle.",
  MaxNumberOfTextures: "Maximum number of texture atlas images produced.",
  MaxNumberOfVertices: "Vertex cap for the textured mesh (decimates above this).",
  MaxTextureSize: "Maximum texture atlas dimension (pixels).",
  MaxTrianglesPerCluster: "Maximum triangles grouped per texturing cluster (memory/perf).",
  MinNumberOfVertices: "Lower vertex bound (0 = no minimum).",
  MinTextureSize: "Minimum texture atlas dimension (pixels).",
  Multiband: "Multiband blending across texture seams for a seamless texture.",
  SharpeningFactor: "Sharpening applied to the final texture.",
  TriangleMargin: "Pixel margin around each triangle in the atlas to prevent colour bleeding.",
  UnwrappingFactor: "Controls the UV unwrapping packing/distortion tradeoff.",

  // --- Wrapper (mesh morph / spatial constraints) ---
  ColoredVerticesAreFixed: "Treat coloured/constrained vertices as fixed anchors during morphing.",
  DistanceWeight: "Weight of the distance term in the wrapper's spatial fit.",
  EnableSpatialConstraints: "Enable spatial constraints in the wrapper's mesh morphing.",
  GlobalIterations: "Global iterations of the wrapper morph/solve.",
  InputMeshSmoothingIterations: "Smoothing passes applied to the input mesh before morphing.",
  MaxVerticesMorpher: "Vertex cap for the morpher step.",
  SmoothnessWeight: "Weight of the smoothness term in the morph; higher = smoother result.",
  SpatialConstraintsDistanceThreshold: "Distance threshold for applying spatial constraints.",
  SpatialConstraintsNormalThreshold: "Normal-angle threshold (degrees) for spatial constraints.",

  // --- Workspace (runtime) ---
  ExportPath: "Output path — set per-scan by the worker at runtime (not editable here).",
  ImagesPath: "Extracted-frames path — set per-scan by the worker at runtime (not editable here).",
  TempPath: "Scratch path — set per-scan by the worker at runtime (not editable here).",
  UpVector: "Which axis is treated as 'up' for the reconstruction.",

  // --- CameraIntrinsics (runtime) ---
  FocalLengthX: "Seed horizontal focal length (px) — set per-scan at runtime for 360 captures; blank lets the engine auto-calibrate.",
  FocalLengthY: "Seed vertical focal length (px) — set per-scan at runtime for 360 captures; blank lets the engine auto-calibrate.",
  PrincipalPointX: "Seed optical-centre X (px) — set per-scan at runtime.",
  PrincipalPointY: "Seed optical-centre Y (px) — set per-scan at runtime.",

  // --- CubeRig (360 cube-face rig) ---
  Mode: "Cube-face rig solve mode: 'fixed' keeps the six cube faces locked as a rigid rig; 'auto' lets bundle adjustment adjust them.",

  // --- shared across stages ---
  UpdateColors: "Recompute colours from the source images for this stage's output.",
  UseMasking: "Honour image masks (ignore masked-out pixels) during this stage.",
};

export function paramDoc(key) {
  return PARAM_DOCS[key] || null;
}

// Valid value ranges, shown under the description in the hover tooltip. Only populated where the
// bound is certain: boolean on/off flags (0–1), percentage fields (0–100), and 0–1 ratios. The
// numeric/iteration/resolution/pixel settings are intentionally left out until 3Dflow's authoritative
// limits are confirmed — add { min, max } entries here as those are nailed down.
export const PARAM_RANGES = {
  // Boolean enable/disable flags (0 = off, 1 = on). Keys are shared across categories.
  AdjustInternals: { min: 0, max: 1 },
  AdjustRadialDistortion: { min: 0, max: 1 },
  AdjustTangentialDistortion: { min: 0, max: 1 },
  TwoStepsFocalAdjustment: { min: 0, max: 1 },
  CropBordersWithConvexHull: { min: 0, max: 1 },
  GenerateOrthophoto: { min: 0, max: 1 },
  UseColorBalance: { min: 0, max: 1 },
  UseMultiband: { min: 0, max: 1 },
  UsePhotoconsistency: { min: 0, max: 1 },
  BlurrinessDetection: { min: 0, max: 1 },
  FinalBa: { min: 0, max: 1 },
  GlobalReconstruction: { min: 0, max: 1 },
  MatchingBreakup: { min: 0, max: 1 },
  SavePartialReconstructions: { min: 0, max: 1 },
  StructSequence: { min: 0, max: 1 },
  UngroupCalibrationsOnFinalBA: { min: 0, max: 1 },
  UseMasking: { min: 0, max: 1 },
  HyperplaneMatching: { min: 0, max: 1 },
  RawOutput: { min: 0, max: 1 },
  UpdateColors: { min: 0, max: 1 },
  SmoothingFilter: { min: 0, max: 1 },
  ColorBalance: { min: 0, max: 1 },
  GenerateFloat: { min: 0, max: 1 },
  Multiband: { min: 0, max: 1 },
  ColoredVerticesAreFixed: { min: 0, max: 1 },
  EnableSpatialConstraints: { min: 0, max: 1 },

  // Percentages (0–100).
  MaxResolutionPercent: { min: 0, max: 100 },
  PCMaxResolutionPercent: { min: 0, max: 100 },

  // Ratios (0–1).
  FarPointsRatio: { min: 0, max: 1 },
  MinInliersRatio: { min: 0, max: 1 },
};

export function paramRange(key) {
  return PARAM_RANGES[key] || null;
}

// Short "why / when you'd change this" guidance, shown under the description. Direction of change plus
// the capture scenario it helps. Best-effort tuning advice — populated for the main tunable settings;
// niche/runtime params are left without a note. Add entries here as the team's tuning knowledge grows.
export const PARAM_TUNING = {
  // --- BundleAdjustment ---
  AdjustInternals: "Turn off when you trust a fixed factory calibration and want a tighter solve; leave on for uncalibrated lenses.",
  AdjustRadialDistortion: "Enable for wide/consumer lenses with visible curvature; disable for pre-rectified imagery.",
  AdjustTangentialDistortion: "Enable only if you see decentering distortion — usually left off as it can destabilise the solve.",
  MaxReprojectionError: "Lower to reject more outliers for a cleaner solve; raise to keep points on noisy or low-overlap captures.",
  MaxIterations: "Raise if alignment hasn't converged on hard scenes; lower to cap solve time.",
  MaxOuterIterations: "Raise for difficult alignments that need more refine passes; lower to save time.",
  MaximumTracksPerCamera: "Raise for sturdier alignment on richly textured scenes; lower to speed up very image-dense captures.",
  RemoveInvalidCameras: "Enable to drop frames that won't align cleanly (e.g. motion-blurred) instead of letting them distort the model.",
  TwoStepsFocalAdjustment: "Enable when focal length is uncertain and the solve drifts — more stability at some cost.",

  // --- Samantha / Scamantha (SfM) ---
  DownscaleFactor: "Raise (2, 4) to speed up alignment or cope with low-texture/huge frames; keep at 1 for maximum detail.",
  MaxResolution: "Lower for faster matching and less RAM; raise to recover detail from high-resolution imagery.",
  MaxKeypointsPerImage: "Raise for low-texture or thin-overlap scenes that fail to align; lower to speed up and cut memory.",
  KeypointNumberOfLevels: "Raise when scale varies a lot (close-ups mixed with wide shots); lower to speed up.",
  NumberOfMatchViews: "Raise for loopy/unordered captures to find more connections; lower for ordered video to save time.",
  NumberOfMaxMatchViews: "Raise to match against more views on unordered sets; lower to bound matching time.",
  MinimumTrackLength: "Raise to keep only well-observed, reliable points (cleaner); lower to retain geometry on thin overlap.",
  ReprojectionThreshold: "Lower for a stricter, cleaner sparse cloud; raise to tolerate noisier matches on hard sets.",
  FinalMaxReprojectionError: "Lower to tighten the final solve for accuracy; raise to keep more points on noisy captures.",
  FinalBa: "Leave on for best accuracy; turn off only to save time when alignment is already solid.",
  GlobalReconstruction: "Try global for unordered photo sets; keep incremental for sequential/video captures.",
  StructSequence: "Enable for video/sequential frames to match only nearby views and save time; disable for unordered sets.",
  NCamerasToFixIntrinsics: "Lower to lock calibration earlier on a consistent rig; raise to keep refining across varied imagery.",
  FarPointsRatio: "Raise to keep distant background geometry; keep at 0 to drop far noise like sky or horizon.",
  AccuracyThreshold: "Lower for stricter matches (cleaner, fewer); raise to accept more on difficult sets.",
  MinInliersRatio: "Raise to reject weak image pairs for robustness; lower to keep marginal pairs when overlap is thin.",

  // --- Stereo (dense cloud) ---
  MaxResolutionPercent: "Raise toward 100 for a denser, more detailed cloud at higher cost/RAM; lower for speed or weak hardware.",
  WindowSize: "Raise for smoother results on low-texture or noisy surfaces; lower to preserve fine detail and edges.",
  NoiseFiltering: "Raise to clean a noisy cloud (reflective/low-texture scenes); lower to keep fine detail.",
  DepthNoiseFiltering: "Raise to suppress depth-map noise on difficult surfaces; lower to retain detail.",
  DepthMapSpeedup: "Raise to speed up dense matching when quality is already fine; lower toward 0 for maximum accuracy.",
  NumberOfNearestCameras: "Raise for more redundancy and robustness in fusion; lower to speed up.",
  AdditionalPyramidLevels: "Raise to handle large low-texture areas like walls or roofs; adds time.",

  // --- Surface (mesh) ---
  EdgeLengthTrimScale: "Lower to aggressively trim stretched triangles bridging gaps (cleaner edges, risks holes); raise to keep more surface.",
  ComponentSizeTrimScale: "Raise to drop small floating fragments and noise islands; keep 0 to preserve everything.",
  PCMaxResolutionPercent: "Raise for a finer, higher-triangle mesh; lower for a lighter mesh and faster meshing.",
  SmoothingIterations: "Increase to reduce surface noise on rough captures; keep low to preserve sharp detail.",
  PCPostDecimationFactor: "Raise to cut triangle count for lighter downstream models; keep 1 for full density.",
  Thickness: "Match to the real surface/shell thickness — affects watertightness on thin structures like piping.",

  // --- Texture ---
  MaxTextureSize: "Raise for sharper texture at the cost of file size/VRAM; lower for lighter web/mobile models.",
  MaxNumberOfTextures: "Raise to carry more detail across atlases on large models; lower to reduce file count.",
  MaxNumberOfVertices: "Lower to decimate heavy meshes for performance; raise to keep full geometry.",
  Multiband: "Keep on for seamless texture across image seams; turn off only for speed.",
  SharpeningFactor: "Raise for crisper texture; too high introduces haloing artefacts.",
  ColorBalance: "Enable when lighting varies across the capture to even out tone; disable if already consistent.",
  BlurrinessDetection: "Enable to keep motion-blurred frames out of the texture/orthophoto.",
  ImageResolutionPercent: "Lower to speed up texturing and shrink textures; raise for maximum texture detail.",

  // --- Wrapper (mesh morph) ---
  SmoothnessWeight: "Raise for a smoother morphed mesh; lower to follow the data more closely.",
  DistanceWeight: "Raise to pull the morph tighter onto the target geometry; balance against smoothness.",
  GlobalIterations: "Raise if the morph hasn't settled; adds time.",
  EnableSpatialConstraints: "Enable to hold the morph to spatial anchors; disable for a freer fit.",
  InputMeshSmoothingIterations: "Increase to pre-clean a noisy input mesh before morphing.",

  // --- CubeRig ---
  Mode: "Use 'auto' to let bundle adjustment refit the cube rig when fixed geometry causes misalignment/warping; 'fixed' is the stable default for clean 360 captures.",

  // --- shared ---
  UseMasking: "Enable to exclude masked clutter (sky, moving objects, rig) from this stage.",
  UsePhotoconsistency: "Enable to sharpen results where source images agree; disable for smoother output on textureless areas.",
};

export function paramTuning(key) {
  return PARAM_TUNING[key] || null;
}
